import Vue from 'vue'
import {
  S,
  G,
  M,
  A,
  MediaComponentProps,
  DeviceList,
  DeviceListGroup,
  SelectedDevices
} from './types'
import { Getters, Mutations, Actions } from 'vuex'
import WebRTCProvider from '@/webrtc'
import * as MediaDeviceTypes from '@/webrtc/constants/mediaDeviceTypes'

/* -------------------------------------------------------------------------- */

const state = (): S => ({
  isCalling: false,
  isCaller: false,
  roomId: '',
  pinCode: '',
  roomSecret: '',
  minimized: false,
  videoActive: true,
  microphoneActive: true,
  shareScreenActive: false,
  participants: [],
  mainScreenParticipant: null,
  requestPendingRoomId: null,
  pendingUsers: {},
  deniedUsers: {},
  caller: null,
  callerOccupation: null,
  videoRoomReady: false,
  mediaDevices: {},
  inCallWebView: false,
  cancelledByCaller: false
})

/* -------------------------------------------------------------------------- */

const getters: Getters<S, G> = {
  isCalling: state => state.isCalling,
  isCaller: state => state.isCaller,
  roomId: state => state.roomId || '',
  pinCode: state => state.pinCode || '',
  roomSecret: state => state.roomSecret || '',
  minimized: state => state.minimized,
  videoActive: state => state.videoActive,
  microphoneActive: state => state.microphoneActive,
  shareScreenActive: state => state.shareScreenActive,
  participants: state => state.participants,
  mainScreenParticipant: state => state.mainScreenParticipant,
  isContainingPatient: state =>
    state.participants.some(({ isPatient }) => isPatient) ||
    Object.keys(state.pendingUsers).some(
      id => !(state.pendingUsers[Number(id)] as any).occupation
    ),
  requestPendingRoomId: state => state.requestPendingRoomId,
  pendingUsers: state => state.pendingUsers,
  deniedUsers: state => state.deniedUsers,
  caller: state => state.caller,
  callerOccupation: state => state.callerOccupation,
  videoRoomReady: state => state.videoRoomReady,
  mediaDevices: state => state.mediaDevices,
  selectedDevices: ({ mediaDevices }) => {
    return (Object.keys(mediaDevices) as Array<MediaDeviceKind>).reduce(
      (devices, deviceType) => {
        const device = (mediaDevices[deviceType] as DeviceList).find(
          d => d.selected
        )
        return device
          ? Object.assign(devices, { [deviceType]: device })
          : devices
      },
      {} as SelectedDevices
    )
  },
  inCallWebView: state => state.inCallWebView,
  cancelledByCaller: state => state.cancelledByCaller
}

/* -------------------------------------------------------------------------- */

const mutations: Mutations<S, M> = {
  switchCalling: (state, on) => {
    state.isCalling = on
  },

  switchCaller: (state, on) => {
    state.isCaller = on
  },

  refreshRoom: (state, { roomId, pinCode, roomSecret }) => {
    state.roomId = roomId
    state.pinCode = pinCode
    state.roomSecret = roomSecret
  },

  clearRoom: state => {
    state.roomId = ''
    state.pinCode = ''
    state.roomSecret = ''
    state.minimized = false
    state.videoActive = true
    state.requestPendingRoomId = null
    state.microphoneActive = true
    state.shareScreenActive = false
    state.participants = []
    state.mainScreenParticipant = null
    state.pendingUsers = {}
    state.deniedUsers = {}
    state.caller = null
    state.callerOccupation = null
    state.mediaDevices = {}
  },

  minimized: (state, on) => {
    state.minimized = on
  },

  videoActive: (state, on) => {
    state.videoActive = on
  },

  microphoneActive: (state, on) => {
    state.microphoneActive = on
  },

  shareScreenActive: (state, on) => {
    state.shareScreenActive = on
  },

  setRequestPendingRoomId: (state, value) => {
    state.requestPendingRoomId = value
  },

  addPendingUser: (state, user) => {
    Vue.set(state.pendingUsers, user.id, user)
  },

  removePendingUser: (state, { id }) => {
    Vue.delete(state.pendingUsers, id)
  },

  clearPendingUsers: state => {
    state.pendingUsers = {}
  },

  removeDeniedUser: (state, { id }) => {
    Vue.delete(state.deniedUsers, id)
  },

  addDeniedUser: (state, user) => {
    state.deniedUsers = Object.assign({}, state.deniedUsers, {
      [user.id]: user
    })
  },

  clearDeniedUsers: state => {
    state.deniedUsers = {}
  },

  setCaller: (state, caller) => {
    state.caller = caller
  },

  setCallerOccupation: (state, occupation) => {
    state.callerOccupation = occupation
  },

  setVideoRoomReady: (state, value) => {
    state.videoRoomReady = value
  },

  addParticipants: (state, participant) => {
    const searchedIndex = state.participants.findIndex(
      p => p.id === participant.id
    )
    const index = searchedIndex > -1 ? searchedIndex : state.participants.length

    const participants = [...state.participants]
    participants[index] = participant
    state.participants = participants

    if (state.participants.length === 1) {
      state.mainScreenParticipant = state.participants[0]
    }
  },

  removeParticipants: (state, { id }) => {
    const index = state.participants.findIndex(p => p.id === id)

    if (index === -1) {
      return
    }
    const participants = [...state.participants]
    participants.splice(index, 1)
    state.participants = participants
  },

  clearParticipants: state => {
    state.participants = []
  },

  setMainScreenParticipant: (state, { id }) => {
    state.mainScreenParticipant = state.participants.find(
      participant => Number(participant.id) === Number(id)
    ) as MediaComponentProps
  },

  refrechMediaDevices: (state, devices) => {
    const mediaDevices: DeviceListGroup = {}

    devices.forEach(device => {
      const kind = device.kind

      if (!mediaDevices[kind]) {
        mediaDevices[kind] = []
      }

      const devices = mediaDevices[kind] as DeviceList

      devices.push({
        id: device.deviceId,
        label: device.label,
        selected: devices.length === 0
      })
    })

    state.mediaDevices = mediaDevices
  },

  setSelectedMediaDevice: (state, { type, id }) => {
    if (!state.mediaDevices[type]) {
      return
    }

    const devices = [...(state.mediaDevices[type] as DeviceList)]

    devices.forEach(device => {
      device.selected = device.id === id
    })

    Vue.set(state.mediaDevices, type, devices)
  },

  setInCallWebView: state => {
    state.inCallWebView = true
  },

  setCancelledByCaller: (state, cancelled) => {
    state.cancelledByCaller = cancelled
  }
}

/* -------------------------------------------------------------------------- */

const actions: Actions<S, A, G, M> = {
  call({ commit }, { users }) {
    commit('switchCaller', true)
    commit('switchCalling', true)

    commit('clearParticipants')
    commit('clearPendingUsers')
    commit('clearDeniedUsers')

    commit('setCancelledByCaller', false)
    users.forEach(user => commit('addPendingUser', user))
  },

  setAsCallee({ commit }, roomInfo) {
    commit('switchCaller', false)
    commit('refreshRoom', roomInfo)
    commit('setCancelledByCaller', false)
  },

  leave({ commit }) {
    commit('switchCalling', false)
    commit('switchCaller', false)
    commit('clearRoom')
  },

  async updateSelectedMediaDevice({ commit }, { type, id }) {
    const webrtc = WebRTCProvider.getConnection()

    if (!webrtc) {
      throw new Error('WebRTC connection is not initialized')
    }

    switch (type) {
      case MediaDeviceTypes.VIDEO_INPUT:
      case MediaDeviceTypes.AUDIO_INPUT:
        await webrtc.setInputDevice(
          MediaDeviceTypes.MediaDevice2Media[type],
          id
        )
        break

      default:
        console.error('Invalid type is passed')
        return
    }

    commit('setSelectedMediaDevice', { type, id })
  },

  setInCallWebView({ commit }) {
    commit('setInCallWebView')
  }
}

/* -------------------------------------------------------------------------- */

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
